import { useState, type ReactNode } from 'react';
import type { JobDetail, OfferSettings } from '@nest-recruit/shared';
import { Card } from '../../../components/Card';
import { Checkbox } from '../../../components/Checkbox';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { Select } from '../../../components/Select';
import { EMPTY } from '../../../lib/format';
import { useOfferChoices } from '../../offers/api/offers-api';
import { SalaryBand } from '../../organisation/components/SalaryBand';
import { useSaveJobStep } from '../api/jobs-api';
import { useStepAutosave } from '../hooks/use-step-autosave';
import { missingAction, sectionMissing } from './builder/SectionMissing';
import { JoiningDocumentsEditor } from './JoiningDocumentsEditor';
import type { StepProps } from './step-props';
import { wholeNumber } from '../../../lib/whole-number';
import styles from './steps.module.css';

/**
 * JOB-08 — step 7, offer settings (F5.11). The band rule never blocks an offer, it adds an
 * approval (S12 Q3). Defaults 7 days and 6 months (Q6). S23 adds the offer letter (ADMIN-16)
 * and the offer approval chain (S12 Q2, Q5) — both needed before the job can open. S24 adds the
 * documents a new hire hands in (Q2).
 */

function draftOf(job: JobDetail): OfferSettings {
  return job.offer;
}

export function OfferStep({ job, isReadOnly, onConflict, footer }: StepProps): ReactNode {
  const [draft, setDraft] = useState<OfferSettings>(() => draftOf(job));
  const saveStep = useSaveJobStep();
  const choices = useOfferChoices(job.ventureId);
  const { state, schedule, flush } = useStepAutosave<OfferSettings>({
    job,
    onConflict,
    save: (offer, version) => saveStep.mutateAsync({ id: job.id, step: 'offer', body: { version, offer } }),
  });
  const errors = state.kind === 'error' ? state.fields : {};

  function change(patch: Partial<OfferSettings>): void {
    const next = { ...draft, ...patch };
    setDraft(next);
    schedule(next);
  }

  return (
    <div className={styles.step}>
      <Card title="Offer settings" meta="Starting values for every offer made on this job" action={missingAction(sectionMissing(job, 'offer'))}>
        <div className={styles.form}>
          <div className={styles.numbers}>
            <Field label="Offer stays open for" error={errors['offer.validityDays']}>
              {({ inputId, isInvalid }) => (
                <Input
                  id={inputId}
                  isInvalid={isInvalid}
                  isNumeric
                  inputMode="numeric"
                  suffix="days"
                  disabled={isReadOnly}
                  value={String(draft.validityDays)}
                  onChange={(event) => change({ validityDays: wholeNumber(event.target.value) })}
                />
              )}
            </Field>
            <Field label="Probation" error={errors['offer.probationMonths']}>
              {({ inputId, isInvalid }) => (
                <Input
                  id={inputId}
                  isInvalid={isInvalid}
                  isNumeric
                  inputMode="numeric"
                  suffix="months"
                  disabled={isReadOnly}
                  value={String(draft.probationMonths)}
                  onChange={(event) => change({ probationMonths: wholeNumber(event.target.value) })}
                />
              )}
            </Field>
            <div className={styles.fact}>
              <span className={styles.factLabel}>Salary band</span>
              <p className={styles.factValue}>{job.position ? <SalaryBand position={job.position} /> : EMPTY}</p>
            </div>
          </div>
          <div>
            <Checkbox
              checked={draft.isBandEnforced}
              disabled={isReadOnly}
              label="Offers outside the salary band need an extra approval"
              onChange={(isBandEnforced) => change({ isBandEnforced })}
            />
            <p className={styles.hint}>
              It isn't blocked: one more person approves it first.
            </p>
          </div>
          <div className={styles.numbers}>
            <Field label="Offer letter" isRequired error={errors['offer.letterId']} hint={(choices.data?.letters.length ?? 1) === 0 ? 'No offer letters yet — HR adds them under Admin, Offer letters.' : undefined}>
              {({ inputId, isInvalid }) => (
                <Select
                  id={inputId}
                  label="Offer letter"
                  placeholder="Choose a letter"
                  isInvalid={isInvalid}
                  disabled={isReadOnly}
                  value={draft.letterId}
                  options={(choices.data?.letters ?? []).map((letter) => ({ value: letter.id, label: letter.name }))}
                  onChange={(letterId) => change({ letterId })}
                />
              )}
            </Field>
            <Field label="Who approves offers" isRequired error={errors['offer.approvalChainId']} hint={(choices.data?.chains.length ?? 1) === 0 ? 'No offer approval chains yet — your admin adds them under Admin, Approval chains.' : undefined}>
              {({ inputId, isInvalid }) => (
                <Select
                  id={inputId}
                  label="Who approves offers"
                  placeholder="Choose an approval chain"
                  isInvalid={isInvalid}
                  disabled={isReadOnly}
                  value={draft.approvalChainId}
                  options={(choices.data?.chains ?? []).map((chain) => ({ value: chain.id, label: chain.name }))}
                  onChange={(approvalChainId) => change({ approvalChainId })}
                />
              )}
            </Field>
          </div>
        </div>
      </Card>

      <Card title="Documents before joining" meta="What a new hire hands in before joining">
        <JoiningDocumentsEditor value={draft.joiningDocuments} isReadOnly={isReadOnly} onChange={(joiningDocuments) => change({ joiningDocuments: [...joiningDocuments] })} />
      </Card>

      {footer({ state, flush })}
    </div>
  );
}
